/**
 * BaseInformationView - Pure presentational component for vessel base information
 *
 * This component displays vessel details without any data fetching hooks.
 * It receives all data via props, making it usable in both:
 * - Main window (wrapped by BaseInformation which handles data fetching)
 * - Popup windows (data passed directly from BroadcastChannel payload)
 *
 * Architecture: Keeps UI components DRY while allowing different data sources.
 */
import React, { useEffect, useRef } from 'react';
import { ShipInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { cn, isNonEmpty } from '@ais-ui/utils';
import { Separator } from '@ais-ui/ui-core';
import {
  Anchor,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  MapPin,
  Navigation,
  Ship,
} from 'lucide-react';
import { LastPortCallData, LastPortCallIntegrated } from '../LastPortCall';
import { AisReportStatus } from '../useAISReportAge';
import { LastOriginDisplay } from '../LastOriginDisplay';

// ============================================================================
// Shared Item Components (no hooks except formatting)
// ============================================================================

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

// ============================================================================
// Identification Section
// ============================================================================

interface IdentificationViewProps {
  shipInfo: ShipInfo;
  extendedVesselType?: string | null;
  showExtendedVesselType?: boolean;
}

const IdentificationView = ({
  shipInfo,
  extendedVesselType,
  showExtendedVesselType = false,
}: IdentificationViewProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.shipDetailsBaseNameLabel}
        value={shipInfo.name}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseIMOLabel}
        value={shipInfo.imo}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={shipInfo.mmsi}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCallSignLabel}
        value={shipInfo.callSign}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCountryLabel}
        value={shipInfo.country}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseAISTypeLabel}
        value={shipInfo.aisType}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseSourceTypeLabel}
        value={shipInfo.sourceType}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseShipTypeLabel}
        value={shipInfo.shipType}
      />
      {showExtendedVesselType && (
        <div className={'flex flex-col mt-1'}>
          <span className="text-muted-foreground text-xs">
            Extended vessel info type
          </span>
          <span className="text-xs whitespace-nowrap select-text">
            {isNonEmpty(extendedVesselType) ? extendedVesselType : '-'}
          </span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Physical Characteristics Section
// ============================================================================

interface PhysicalCharacteristicsItemProps {
  label: string;
  value: string;
}

const PhysicalCharacteristicsItem = ({
  label,
  value,
}: PhysicalCharacteristicsItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

interface PhysicalCharacteristicsViewProps {
  shipInfo: ShipInfo;
  convertLengthString?: (value: string) => string;
}

const PhysicalCharacteristicsView = ({
  shipInfo,
  convertLengthString,
}: PhysicalCharacteristicsViewProps) => {
  const { messages } = useLocalization();
  const convert = convertLengthString || ((v: string) => v);

  return (
    <div className="grid grid-cols-3 gap-1">
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseWidthLabel}
        value={convert(shipInfo.width)}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseLengthLabel}
        value={convert(shipInfo.length)}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseDraughtLabel}
        value={convert(shipInfo.draught)}
      />
    </div>
  );
};

// ============================================================================
// Navigation Information Section
// ============================================================================

interface NavigationalInformationItemProps {
  label: string;
  value: string;
}

const NavigationalInformationItem = ({
  label,
  value,
}: NavigationalInformationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const DirectionArrow = ({ degrees }: { degrees: string }) => {
  if (!isNonEmpty(degrees)) {
    return null;
  }

  const degreesNum = parseFloat(degrees);

  if (isNaN(degreesNum)) {
    return null;
  }

  const rotationDegrees = degreesNum - 45;
  const offsetRadians = (degreesNum * Math.PI) / 180;
  const cosValue = Math.cos(offsetRadians);
  const offsetMultiplier =
    Math.sign(cosValue) * Math.pow(Math.abs(cosValue), 2);
  const verticalOffset = offsetMultiplier * 2;

  return (
    <Navigation
      className="h-3 w-3 text-blue-600 inline ml-2"
      style={{
        transform: `translateY(${verticalOffset}px) rotate(${rotationDegrees}deg)`,
      }}
    />
  );
};

const NavigationalInformationItemWithDirection = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  let formattedValue = '-';
  if (isNonEmpty(value)) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      formattedValue = numValue.toFixed(1) + '°';
    } else {
      formattedValue = value;
    }
  }

  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs flex items-center whitespace-nowrap select-text">
        <span>{formattedValue}</span>
        <DirectionArrow degrees={value} />
      </span>
    </div>
  );
};

const SpeedTrendIndicator = ({
  current,
  previous,
}: {
  current: string;
  previous: string | null;
}) => {
  if (!previous || !isNonEmpty(current) || !isNonEmpty(previous)) {
    return null;
  }

  const currentSpeed = parseFloat(current);
  const previousSpeed = parseFloat(previous);

  if (isNaN(currentSpeed) || isNaN(previousSpeed)) {
    return null;
  }

  const diff = currentSpeed - previousSpeed;
  const threshold = 0.1;

  if (Math.abs(diff) < threshold) {
    return <ArrowRight className="h-3 w-3 text-muted-foreground inline ml-1" />;
  } else if (diff > 0) {
    return <ArrowUp className="h-3 w-3 text-green-600 inline ml-1 rotate-45" />;
  } else {
    return (
      <ArrowDown className="h-3 w-3 text-red-600 inline ml-1 -rotate-45" />
    );
  }
};

const NavigationalInformationItemWithTrend = ({
  label,
  value,
  previousValue,
}: {
  label: string;
  value: string;
  previousValue: string | null;
}) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs flex items-center whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : '-'}
        <SpeedTrendIndicator current={value} previous={previousValue} />
      </span>
    </div>
  );
};

interface NavigationalInformationViewProps {
  shipInfo: ShipInfo;
}

const NavigationalInformationView = ({
  shipInfo,
}: NavigationalInformationViewProps) => {
  const { messages } = useLocalization();
  const previousSpeedRef = useRef<string | null>(null);

  const previousSpeed = previousSpeedRef.current;

  useEffect(() => {
    previousSpeedRef.current = shipInfo.speedOverGround;
  });

  return (
    <div className="grid grid-cols-2 gap-1">
      <NavigationalInformationItemWithDirection
        label={messages.shipDetailsBaseHeadingLabel}
        value={shipInfo.heading}
      />
      <NavigationalInformationItem
        label={messages.shipDetailsBaseROTLabel}
        value={shipInfo.rateOfTurn}
      />
      <NavigationalInformationItemWithDirection
        label={messages.shipDetailsBaseCOGLabel}
        value={shipInfo.courseOverGround}
      />
      <NavigationalInformationItemWithTrend
        label={messages.shipDetailsBaseSOGLabel}
        value={shipInfo.speedOverGround}
        previousValue={previousSpeed}
      />
    </div>
  );
};

// ============================================================================
// Position Item Component (simplified - no useSelectionState)
// ============================================================================

interface PositionItemViewProps {
  label: string;
  latitude: string;
  longitude: string;
  timestamp: Date | undefined | null;
  ageStatus?: AisReportStatus;
}

const PositionItemView = ({
  label,
  latitude,
  longitude,
  timestamp,
  ageStatus,
}: PositionItemViewProps) => {
  const hasValidCoordinates = isNonEmpty(latitude) && isNonEmpty(longitude);
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const getStatusColor = (status?: AisReportStatus) => {
    switch (status) {
      case AisReportStatus.Fresh:
        return 'text-green-600';
      case AisReportStatus.Warning:
        return 'text-yellow-600';
      case AisReportStatus.Removed:
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="col-span-2">
      <div className={'flex flex-col mt-1'}>
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="text-xs whitespace-nowrap select-text">
          {hasValidCoordinates ? `${latitude}, ${longitude}` : '-'}
        </span>
        {timestamp && (
          <span
            className={cn(
              'text-xs tabular-nums whitespace-nowrap select-text mt-1',
              getStatusColor(ageStatus),
            )}
          >
            {formatDateTimeWithTimeZone(timestamp)}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Operational Status Section
// ============================================================================

interface OperationalStatusViewProps {
  shipInfo: ShipInfo;
  positionAgeStatuses?: {
    last?: AisReportStatus;
    terrestrial?: AisReportStatus;
    satellite?: AisReportStatus;
  };
}

const OperationalStatusView = ({
  shipInfo,
  positionAgeStatuses,
}: OperationalStatusViewProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const getLastPosition = () => {
    const aisTime = shipInfo.lastAisReport
      ? new Date(shipInfo.lastAisReport).getTime()
      : 0;
    const aisSatTime = shipInfo.lastAisSatReport
      ? new Date(shipInfo.lastAisSatReport).getTime()
      : 0;

    if (aisTime >= aisSatTime) {
      return {
        latitude: shipInfo.lastAisLatitude,
        longitude: shipInfo.lastAisLongitude,
        timestamp: shipInfo.lastAisReport,
      };
    } else {
      return {
        latitude: shipInfo.lastAisSatLatitude,
        longitude: shipInfo.lastAisSatLongitude,
        timestamp: shipInfo.lastAisSatReport,
      };
    }
  };

  const lastPosition = getLastPosition();

  return (
    <div className="grid grid-cols-2 gap-1">
      <NavigationalInformationItem
        label={messages.shipDetailsBaseDestinationLabel}
        value={shipInfo.destination}
      />
      <NavigationalInformationItem
        label={messages.shipDetailsBaseNavigationStatusLabel}
        value={shipInfo.navigationalStatus}
      />
      <NavigationalInformationItem
        label={messages.shipDetailsBaseETATitle}
        value={formatDateTimeWithTimeZone(shipInfo.eta)}
      />
      {shipInfo.portEta && (
        <NavigationalInformationItem
          label={messages.shipDetailsBasePortETATitle}
          value={formatDateTimeWithTimeZone(shipInfo.portEta)}
        />
      )}
      <PositionItemView
        label="Last Position"
        latitude={lastPosition.latitude}
        longitude={lastPosition.longitude}
        timestamp={lastPosition.timestamp}
        ageStatus={positionAgeStatuses?.last}
      />

      <PositionItemView
        label="AIS Terrestial Position"
        latitude={shipInfo.lastAisLatitude}
        longitude={shipInfo.lastAisLongitude}
        timestamp={shipInfo.lastAisReport}
        ageStatus={positionAgeStatuses?.terrestrial}
      />

      <PositionItemView
        label="AIS Satellite Position"
        latitude={shipInfo.lastAisSatLatitude}
        longitude={shipInfo.lastAisSatLongitude}
        timestamp={shipInfo.lastAisSatReport}
        ageStatus={positionAgeStatuses?.satellite}
      />
    </div>
  );
};

// ============================================================================
// Main View Component
// ============================================================================

export interface BaseInformationViewProps {
  shipInfo: ShipInfo;
  /** Last port call data (pre-computed) */
  lastPortCallData?: LastPortCallData | null;
  /** Whether to show the last port call section */
  showLastPortCall?: boolean;
  /** Loading state for last port call */
  isLoadingLastPortCall?: boolean;
  /** Extended vessel type from ship database */
  extendedVesselType?: string | null;
  /** Whether to show extended vessel type */
  showExtendedVesselType?: boolean;
  /** Position age statuses (pre-computed) */
  positionAgeStatuses?: {
    last?: AisReportStatus;
    terrestrial?: AisReportStatus;
    satellite?: AisReportStatus;
  };
  /** Optional length unit converter function */
  convertLengthString?: (value: string) => string;
}

export const BaseInformationView = ({
  shipInfo,
  lastPortCallData,
  showLastPortCall = false,
  isLoadingLastPortCall = false,
  extendedVesselType,
  showExtendedVesselType = false,
  positionAgeStatuses,
  convertLengthString,
}: BaseInformationViewProps) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium flex items-center gap-2">
            <Ship className="size-4" />
            Identification and Basic Details
          </h3>
          <Separator className="mt-2" />
        </div>
        <IdentificationView
          shipInfo={shipInfo}
          extendedVesselType={extendedVesselType}
          showExtendedVesselType={showExtendedVesselType}
        />
      </div>

      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium flex items-center gap-2">
            <Ship className="size-4" />
            Physical Characteristics
          </h3>
          <Separator className="mt-2" />
        </div>
        <PhysicalCharacteristicsView 
          shipInfo={shipInfo} 
          convertLengthString={convertLengthString}
        />
      </div>

      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium flex items-center gap-2">
            <Navigation className="size-4" />
            Navigation Information
          </h3>
          <Separator className="mt-2" />
        </div>
        <NavigationalInformationView shipInfo={shipInfo} />
      </div>

      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium flex items-center gap-2">
            <Anchor className="size-4" />
            Operational Status
          </h3>
          <Separator className="mt-2" />
        </div>
        <OperationalStatusView
          shipInfo={shipInfo}
          positionAgeStatuses={positionAgeStatuses}
        />
      </div>

      {showLastPortCall && (
        <div className="grid gap-1">
          <div>
            <h3 className="text-base font-medium flex items-center gap-2 mb-2">
              <MapPin className="size-4" />
              Last Port Call
            </h3>
          </div>
          {isLoadingLastPortCall ? (
            <div className="text-xs text-muted-foreground">Loading...</div>
          ) : (
            <LastPortCallIntegrated
              portCall={lastPortCallData ?? null}
              imo={shipInfo.imo}
            />
          )}
        </div>
      )}

      {shipInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={shipInfo.lastOrigin} />
      )}
    </div>
  );
};
